import {
  CATEGORY_LABELS,
  SHORT_FORMAT_LIMIT,
  formatFreshness,
  formatOpportunity,
  type ContentFormat,
  type ContentOpportunity,
} from '../content-engine/opportunities';
import { CopyButton } from './CopyButton';

const PRIORITY_CLASS: Record<ContentOpportunity['priority'], string> = {
  Élevée: 'high',
  Moyenne: 'medium',
  Faible: 'low',
};

interface OpportunityCardProps {
  opportunity: ContentOpportunity;
  format: ContentFormat;
  formatLabel: string;
  leagueName: string | null;
  used: boolean;
  onToggleUsed: (opportunity: ContentOpportunity) => void;
  now?: number;
}

export function OpportunityFacts({ opportunity }: { opportunity: ContentOpportunity }) {
  if (!opportunity.facts.length) return null;
  return (
    <dl className="ci-facts">
      {opportunity.facts.map((fact) => (
        <div key={`${fact.label}-${fact.value}`}>
          <dt>{fact.label}</dt>
          <dd>{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function OpportunityBadges({
  opportunity,
  used,
  now,
}: {
  opportunity: ContentOpportunity;
  used: boolean;
  now?: number;
}) {
  const freshness = formatFreshness(opportunity.eventTime, now);
  return (
    <div className="ci-opp-card__badges">
      <span className={`ci-tag ci-tag--${opportunity.category}`}>
        {CATEGORY_LABELS[opportunity.category]}
      </span>
      <span className={`ci-badge ci-badge--${PRIORITY_CLASS[opportunity.priority]}`}>
        Priorité {opportunity.priority.toLowerCase()}
      </span>
      {freshness && <span className="ci-opp-card__time">{freshness}</span>}
      {used && <span className="ci-opp-card__used">Déjà utilisé</span>}
    </div>
  );
}

export function OpportunityCard({
  opportunity,
  format,
  formatLabel,
  leagueName,
  used,
  onToggleUsed,
  now,
}: OpportunityCardProps) {
  const text = formatOpportunity(opportunity, format, leagueName);
  const tooLong = format === 'short' && text.length > SHORT_FORMAT_LIMIT;
  return (
    <article
      className={`ci-opp-card${used ? ' is-used' : ''}`}
      data-testid={`ci-opportunity-${opportunity.id}`}
    >
      <OpportunityBadges opportunity={opportunity} used={used} now={now} />
      <h3 className="ci-opp-card__title">{opportunity.title}</h3>
      <p className="ci-opp-card__reason">{opportunity.reason}</p>
      {opportunity.angle && <p className="ci-opp-card__angle">{opportunity.angle}</p>}
      <OpportunityFacts opportunity={opportunity} />
      <pre className="ci-content-card__body ci-opp-card__preview">{text}</pre>
      <p className={`ci-opp-card__length${tooLong ? ' is-over' : ''}`}>
        {text.length} caractères
        {tooLong ? ` · dépasse ${SHORT_FORMAT_LIMIT} caractères, à raccourcir pour X` : ''}
      </p>
      <div className="ci-opp-card__actions">
        <CopyButton
          text={text}
          label={`Copier (${formatLabel.toLowerCase()})`}
          ariaLabel={`Copier « ${opportunity.title} » au format ${formatLabel}`}
        />
        <button
          type="button"
          className="ci-btn ci-btn--ghost ci-btn--sm"
          aria-pressed={used}
          onClick={() => onToggleUsed(opportunity)}
        >
          {used ? 'Retirer la marque' : 'Marquer comme utilisé'}
        </button>
      </div>
      <details className="ci-opp-card__sources">
        <summary>Détails techniques</summary>
        <p>
          Score {opportunity.score}/100 · Période : {opportunity.period}
          <br />
          Sources : {opportunity.sources.join(', ')}
        </p>
      </details>
    </article>
  );
}
